/**
 * Kleda Client App
 * Tapijtreiniging klantportaal
 *
 * V2: Portaal-architectuur met persistente Timeline + OrderSummary
 */

import { useState } from 'react'
import { useToken, useOrder, useView, useKiesAfspraak, useWijzigAfspraak, useAnnuleer, useReactiveer, useOpmerkingen, useHighlight, usePageType, useVoorkeuze } from './hooks'
import type { AnnuleerResponseData } from './types'
import { SkeletonKiesAfspraakView } from './components/UI'
import { PageLayout, PortalLayout } from './components/Layout'
import { VroegKeuze } from './components/VroegKeuze'
import {
  KiesAfspraakView,
  AfspraakBevestigdView,
  AlGekozenView,
  GeplandView,
  InBehandelingView,
  ZelfBrengenView,
  ZelfHalenView,
  AfgerondView,
  GeannuleerdView,
  ErrorView,
  OnbekendView
} from './components/Views'
import { VoorinspectieView } from './components/Voorinspectie'
import { FotosView } from './components/FotosView'
import { OndertapijtView } from './components/OndertapijtView'
import { VolgendeKeerKaart } from './components/VolgendeKeerKaart'
import { MijnKledaView } from './components/MijnKledaView'
import { InlogView, detectInlogPad } from './components/InlogView'

const MIJN_KLEDA_AAN = import.meta.env.VITE_MIJN_KLEDA_ENABLED === 'true'
// Inloggen op Mijn Kleda (plan 2026-10-28, golf 1): /inloggen en /inlog/:t.
const MIJN_KLEDA_INLOG_AAN = import.meta.env.VITE_MIJN_KLEDA_INLOG_ENABLED === 'true'
import { ReparatieOfferteView } from './components/ReparatieOfferteView'
import { RisicoBesluitView } from './components/RisicoBesluitView'
import { KlachtBanner } from './components/KlachtBanner'
import { KlachtView } from './components/KlachtView'
import { vlekkenbaasTeBieden } from './components/VlekkenbaasUpsell'
import { isKlachtOpen } from './lib/klacht-types'

/**
 * Detect /reparatie/:token path-based route. Vercel rewrites alle non-/api
 * paths naar index.html, dus we lezen pathname zelf.
 */
function detectReparatieToken(): string | null {
  if (typeof window === 'undefined') return null
  const m = window.location.pathname.match(/^\/reparatie\/([^/?#]+)/)
  return m ? decodeURIComponent(m[1]) : null
}

/**
 * Detect /risico/:token path-based route. Eigen token-bron
 * (Wasserij_item__c.Risico_Token__c) en eigen API-endpoints.
 */
function detectRisicoToken(): string | null {
  if (typeof window === 'undefined') return null
  const m = window.location.pathname.match(/^\/risico\/([^/?#]+)/)
  return m ? decodeURIComponent(m[1]) : null
}

export default function App() {
  // Hooks vóór alle early-returns — voorkomt React #310 ("more hooks than
  // previous render") wanneer eerst een sub-route render't en daarna de
  // hoofdroute (zie Fase 1b post-deploy fix #1).
  const token = useToken()
  const [showKlachtDetail, setShowKlachtDetail] = useState(false)

  // Success-stap na het bevestigen van een afspraak. Leeft hier (boven de
  // status-switch) omdat de refetch de status direct omklapt naar 'ingepland'
  // en de view anders wegspringt vóór de klant de bevestiging (en bij
  // afleveren het Vlekkenbaas-moment) heeft gezien.
  const [bevestigdeAfspraak, setBevestigdeAfspraak] = useState<{ moment: string | null; isPickup: boolean } | null>(null)

  // Path-based risico route: /risico/:token. Klant-besluit-pagina voor
  // risico-meldingen tijdens voorinspectie. Render direct.
  const risicoToken = detectRisicoToken()
  if (risicoToken) {
    return <RisicoBesluitView token={risicoToken} />
  }

  // Path-based reparatie route: /reparatie/:token. Eigen token-bron
  // (Reparatie_Opdracht__c.Token__c) en eigen API-endpoints — geen gedeelde
  // OrderData state. Render direct, vóór de standaard portal-flow.
  const reparatieToken = detectReparatieToken()
  if (reparatieToken) {
    return <ReparatieOfferteView token={reparatieToken} />
  }

  // Path-based inlog-routes van Mijn Kleda: /inloggen en /inlog/:t. Geen
  // ordertoken nodig; na het inwisselen gaat de klant naar
  // /?token=<laatste order>&type=mijn-kleda.
  const inlogPad = MIJN_KLEDA_INLOG_AAN ? detectInlogPad() : null
  if (inlogPad) {
    return <InlogView pad={inlogPad} />
  }

  const {
    order, loading, error, refetch, refetchSilent, refetchWithRoutes,
    markPaymentInitiated, isPaymentPolling, paymentTimedOut, resetPaymentTimedOut,
  } = useOrder(token)
  const viewConfig = useView(order)
  const kiesAfspraak = useKiesAfspraak(token, order, refetchSilent)
  const wijzigAfspraak = useWijzigAfspraak(token, order, refetch)
  const annuleer = useAnnuleer(token, refetch)
  const reactiveer = useReactiveer(token, refetch)
  const opmerkingen = useOpmerkingen(token, order?.phaseType || undefined, refetch)
  const highlight = useHighlight()
  const pageType = usePageType()
  const voorkeuze = useVoorkeuze()
  // ?vroeg=ophalen|afleveren: de keuzelink uit het SMS-bericht "we lopen voor"
  // (plan 2026-10-12). Alleen tonen zolang de order in die fase zit.
  const vroegParam = new URLSearchParams(window.location.search).get('vroeg')
  const vroegKant: 'ophalen' | 'afleveren' | null =
    vroegParam === 'ophalen' && order?.phaseType === 'pickup' ? 'ophalen'
    : vroegParam === 'afleveren' && order?.phaseType === 'delivery' ? 'afleveren'
    : null

  // Helper to navigate back to report (remove type param)
  const navigateToReport = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete('type')
    window.history.replaceState({}, '', url.toString())
    window.location.reload()
  }

  // Loading state - met skeleton loader voor betere UX
  if (loading) {
    return (
      <PageLayout>
        <SkeletonKiesAfspraakView />
      </PageLayout>
    )
  }

  // Error state
  if (error || !order) {
    return (
      <ErrorView
        error={error || 'Kon gegevens niet laden'}
        onRetry={refetch}
      />
    )
  }

  // Mijn Kleda (?type=mijn-kleda, plan 2026-10-26): alles van de klant op
  // één pagina, op het token van deze order. Gaat vóór de order-views: het is
  // een klantpagina, geen orderpagina. Achter VITE_MIJN_KLEDA_ENABLED; uit =
  // gewoon het overzicht van de order.
  if (pageType === 'mijn-kleda' && MIJN_KLEDA_AAN && token) {
    return (
      <PageLayout orderNummer={order.werkOrderNummer}>
        <MijnKledaView token={token} werkOrderId={order.werkOrderId} heeftMobiel={!!order.mobiel} />
      </PageLayout>
    )
  }

  // Geannuleerde order: eigen gefocuste pagina (geen voortgangslijn die
  // "opgehaald" suggereert). Heropenen zet de WO terug naar 'Ophalen plannen';
  // de refetch laat de klant dan direct een nieuw ophaalmoment kiezen. Gaat
  // vóór de deeplink-pagina's: een geannuleerde order toont altijd geannuleerd.
  if (order.status === 'Geannuleerd') {
    return (
      <GeannuleerdView
        order={order}
        onReactiveer={reactiveer.submit}
        isReactiveren={reactiveer.isSubmitting}
        reactiveerError={reactiveer.error}
      />
    )
  }

  // Standalone Ondertapijt page - when ?type=ondertapijt
  // Always show OndertapijtView when type param is set; the view handles missing data gracefully
  if (pageType === 'ondertapijt') {
    return (
      <OndertapijtView
        order={order}
        token={token || ''}
        onRefresh={refetch}
        onBackToReport={navigateToReport}
      />
    )
  }

  // Volgende reiniging (?type=terugkom): de links uit S10 (ritmechips van het
  // kleed-abonnement, voorgekozen via ?interval=), de dag-7 care-guide en
  // WhatsApp. De kaart toont de keuze; de klant bevestigt met één knop.
  if (pageType === 'terugkom') {
    return (
      <PortalLayout order={order}>
        <VolgendeKeerKaart
          token={token || ''}
          werkOrderId={order.werkOrderId}
          heeftMobiel={!!order.mobiel}
          bron={voorkeuze.bron}
          voorkeuze={{ seizoen: voorkeuze.seizoen, abonnement: voorkeuze.abonnement, interval: voorkeuze.interval }}
          standalone
        />
        <div className="mt-md">
          <button type="button" onClick={navigateToReport} className="text-body-sm text-ink-muted underline">Terug naar je overzicht</button>
        </div>
      </PortalLayout>
    )
  }

  // Foto-ingang (?view=fotos): tussenstap naar de timeline(s). Staat vóór de
  // voorinspectie-tak, want die claimt ook de status 'Aan het reinigen' en zou
  // deze deeplink anders opslokken. De view hangt bewust NIET aan
  // voorinspectie.done: een kleed met alleen een reinigingsvideo hoort er ook in.
  if (pageType === 'fotos') {
    return (
      <PortalLayout order={order}>
        <FotosView order={order} token={token || ''} />
      </PortalLayout>
    )
  }

  // Voorinspectie view - show when:
  // 1. ?view=voorinspectie URL param is set (from email CTA) and data exists, OR
  // 2. Status is 'Aan het reinigen' and inspection is done (default behavior)
  if (order.voorinspectie?.done && (pageType === 'voorinspectie' || order.status === 'Aan het reinigen')) {
    return (
      <PortalLayout order={order}>
        <VoorinspectieView
          order={order}
          token={token || ''}
          highlightOndertapijt={highlight === 'ondertapijt'}
          onRefresh={refetch}
        />
      </PortalLayout>
    )
  }

  // Success-stap na bevestigen — gefocuste tussenstap zónder sidebar
  // (details + betalen volgen op de gepland-pagina)
  if (bevestigdeAfspraak) {
    return (
      <AfspraakBevestigdView
        order={order}
        token={token || ''}
        moment={bevestigdeAfspraak.moment}
        isPickup={bevestigdeAfspraak.isPickup}
        onVerder={() => setBevestigdeAfspraak(null)}
        onVlekkenbaasSuccess={refetchSilent}
      />
    )
  }

  // Klacht banner — alleen tonen voor open statussen (Geregistreerd /
  // In_Behandeling / Wacht_op_Klant / Opgelost). 'Gesloten' verbergt de banner.
  const showKlachtBanner = order.klacht && isKlachtOpen(order.klacht.status)

  // Render view within PortalLayout (Timeline + OrderSummary persistent)
  return (
    <>
      <PortalLayout
        order={order}
        token={token || ''}
        vlekkenbaasHighlighted={highlight === 'vlekkenbaas'}
        onVlekkenbaasSuccess={refetchSilent}
        onPaymentInitiated={markPaymentInitiated}
        isPaymentPolling={isPaymentPolling}
        paymentTimedOut={paymentTimedOut}
        resetPaymentTimedOut={resetPaymentTimedOut}
        onOpenKlachtDetail={order.klacht ? () => setShowKlachtDetail(true) : undefined}
        onRefresh={refetch}
      >
        {vroegKant && token && (
          <VroegKeuze token={token} kant={vroegKant} onKlaar={refetchSilent} />
        )}
        {showKlachtBanner && order.klacht && (
          <KlachtBanner klacht={order.klacht} onOpenDetail={() => setShowKlachtDetail(true)} />
        )}
        <ViewContent
          viewConfig={viewConfig}
          order={order}
          token={token || ''}
          onKiesAfspraak={kiesAfspraak.submit}
          onAfspraakBevestigd={(moment) => {
            const isPickup = order.phaseType === 'pickup'
            // De aflever-tussenstap bestáát alleen voor het Vlekkenbaas-moment.
            // Zit die al in de order, dan is het een scherm zonder keuze; de
            // gepland-pagina toont de bevestiging dan zelf ("Afspraak
            // bevestigd" + datum + betalen). Gemeten 20-08 op WB-322242.
            if (!isPickup && !vlekkenbaasTeBieden(order)) return
            setBevestigdeAfspraak({ moment, isPickup })
          }}
          onWijzigAfspraak={wijzigAfspraak.submit}
          onAnnuleer={annuleer.submit}
          onLaadMeerOpties={refetchWithRoutes}
          onSaveOpmerkingen={opmerkingen.submit}
          isSavingOpmerkingen={opmerkingen.isSubmitting}
          onRefresh={refetch}
          vlekkenbaasHighlighted={highlight === 'vlekkenbaas'}
          onVlekkenbaasSuccess={refetchSilent}
          onPaymentInitiated={markPaymentInitiated}
          isPaymentPolling={isPaymentPolling}
          paymentTimedOut={paymentTimedOut}
          resetPaymentTimedOut={resetPaymentTimedOut}
        />
      </PortalLayout>

      {/* Klacht-detail overlay — geopend vanuit banner */}
      {showKlachtDetail && order.klacht && (
        <KlachtView klacht={order.klacht} onClose={() => setShowKlachtDetail(false)} />
      )}
    </>
  )
}

// =============================================================================
// VIEW CONTENT — Renders the right view based on order status
// =============================================================================

interface ViewContentProps {
  viewConfig: { view: string; title: string }
  order: import('./types').OrderData
  token: string
  onKiesAfspraak: (routeId: string, opmerkingen?: string) => Promise<void>
  onAfspraakBevestigd?: (moment: string | null) => void
  onWijzigAfspraak: (routeId: string, opmerkingen?: string) => Promise<void>
  onAnnuleer: (reden?: string) => Promise<AnnuleerResponseData | null>
  onLaadMeerOpties: () => Promise<void>
  onSaveOpmerkingen: (opmerkingen: string, verwacht: string | null) => Promise<void>
  isSavingOpmerkingen: boolean
  onRefresh: () => void
  vlekkenbaasHighlighted?: boolean
  onVlekkenbaasSuccess?: () => void
  onPaymentInitiated?: () => void
  isPaymentPolling?: boolean
  paymentTimedOut?: boolean
  resetPaymentTimedOut?: () => void
}

function ViewContent({
  viewConfig,
  order,
  token,
  onKiesAfspraak,
  onAfspraakBevestigd,
  onWijzigAfspraak,
  onAnnuleer,
  onLaadMeerOpties,
  onSaveOpmerkingen,
  isSavingOpmerkingen,
  onRefresh,
  vlekkenbaasHighlighted,
  onVlekkenbaasSuccess,
  onPaymentInitiated,
  isPaymentPolling,
  paymentTimedOut,
  resetPaymentTimedOut,
}: ViewContentProps) {
  switch (viewConfig.view) {
    case 'kies-afspraak':
      return (
        <KiesAfspraakView
          order={order}
          token={token}
          onKiesAfspraak={onKiesAfspraak}
          onAfspraakBevestigd={onAfspraakBevestigd}
          onLaadMeerOpties={onLaadMeerOpties}
          onRefresh={onRefresh}
          vlekkenbaasHighlighted={vlekkenbaasHighlighted}
          onVlekkenbaasSuccess={onVlekkenbaasSuccess}
        />
      )

    case 'al-gekozen':
      return (
        <AlGekozenView
          order={order}
          token={token}
          onRefresh={onRefresh}
        />
      )

    case 'gepland':
      return (
        <GeplandView
          key={`${order.werkOrderId}:${order.phaseType}`}
          order={order}
          token={token}
          onWijzigAfspraak={onWijzigAfspraak}
          onAnnuleer={onAnnuleer}
          onLaadMeerOpties={onLaadMeerOpties}
          onSaveOpmerkingen={onSaveOpmerkingen}
          isSavingOpmerkingen={isSavingOpmerkingen}
          onRefresh={onRefresh}
          vlekkenbaasHighlighted={vlekkenbaasHighlighted}
          onVlekkenbaasSuccess={onVlekkenbaasSuccess}
          onPaymentInitiated={onPaymentInitiated}
          isPaymentPolling={isPaymentPolling}
          paymentTimedOut={paymentTimedOut}
          resetPaymentTimedOut={resetPaymentTimedOut}
        />
      )

    case 'in-behandeling':
      return <InBehandelingView order={order} />

    case 'zelf-brengen':
      return (
        <ZelfBrengenView
          key={order.werkOrderId}
          order={order}
          onSaveOpmerkingen={onSaveOpmerkingen}
          isSavingOpmerkingen={isSavingOpmerkingen}
        />
      )

    case 'zelf-halen':
      return (
        <ZelfHalenView
          order={order}
          token={token}
          onRefresh={onRefresh}
          vlekkenbaasHighlighted={vlekkenbaasHighlighted}
          onVlekkenbaasSuccess={onVlekkenbaasSuccess}
          onPaymentInitiated={onPaymentInitiated}
          isPaymentPolling={isPaymentPolling}
          paymentTimedOut={paymentTimedOut}
          resetPaymentTimedOut={resetPaymentTimedOut}
        />
      )

    case 'afgerond':
      return <AfgerondView order={order} token={token} />

    default:
      return <OnbekendView />
  }
}
